<mat-card class="modern-camera-card">
    <!-- Card Header -->
    <div class="card-header-wrapper">
        <div class="header-main">
            <!-- Camera Avatar with Live Indicator -->
            <div class="camera-avatar-container">
                <div class="camera-avatar" [class.online]="isOnline" [class.offline]="!isOnline">
                    <mat-icon>videocam</mat-icon>
                </div>
                <span class="status-indicator-dot" [class.online]="isOnline" [class.offline]="!isOnline"
                      [matTooltip]="isOnline ? 'Online (Heartbeat active)' : 'Offline (No recent heartbeat)'">
                </span>
            </div>

            <!-- Title & Model Info -->
            <div class="title-details">
                <div class="title-row">
                    <h3 class="camera-name">{{ camera.deviceName || 'LPR Camera' }}</h3>
                    <!-- Online / Offline Liveness Badge -->
                    <div class="liveness-badge" [class.online]="isOnline" [class.offline]="!isOnline">
                        <mat-icon class="liveness-icon">{{ isOnline ? 'wifi' : 'wifi_off' }}</mat-icon>
                        <span class="liveness-state">{{ isOnline ? 'Online' : 'Offline' }}</span>
                        <span class="liveness-time">• {{ heartbeatRecencyText }}</span>
                    </div>
                </div>

                <div class="meta-row">
                    @if (cameraConfig?.type) {
                    <span class="type-pill">{{ cameraConfig?.type }}</span>
                    }
                    @if (cameraConfig?.model) {
                    <span class="model-text">{{ cameraConfig?.model }}</span>
                    }
                    @if (camera.deviceDescription) {
                    <span class="desc-text truncate max-w-xs" [title]="camera.deviceDescription">
                        • {{ camera.deviceDescription }}
                    </span>
                    }
                </div>
            </div>
        </div>
    </div>

    <mat-card-content class="card-body">
        <!-- Hero Network & Hardware Strip -->
        <div class="network-hardware-strip">
            <!-- Camera ID (Primary & Essential for copying) -->
            <div class="strip-item">
                <span class="strip-label">Camera ID</span>
                <div class="strip-value-row">
                    <code class="hardware-code text-indigo-700 bg-indigo-50 border-indigo-200 font-bold" [title]="camera.id">{{ camera.id }}</code>
                    <button type="button" class="copy-btn text-indigo-600 hover:bg-indigo-100" (click)="copy(camera.id, 'Camera ID')"
                            matTooltip="Copy Camera ID">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
            </div>

            <!-- IP & Endpoint -->
            <div class="strip-item">
                <span class="strip-label">IP & Endpoint</span>
                <div class="strip-value-row">
                    <code class="endpoint-code">{{ ipWithPort || 'No IP configured' }}</code>
                    @if (cameraConfig?.useHttps !== undefined) {
                    <span class="protocol-badge" [class.https]="cameraConfig?.useHttps">
                        {{ cameraConfig?.useHttps ? 'HTTPS' : 'HTTP' }}
                    </span>
                    }
                    @if (cameraConfig?.ipAddress) {
                    <button type="button" class="copy-btn" (click)="copy(ipWithPort || '', 'IP Address')"
                            matTooltip="Copy IP:Port">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                    }
                </div>
            </div>

            <!-- Hardware ID (Displayed for reference) -->
            @if (camera.deviceHardwareID) {
            <div class="strip-item">
                <span class="strip-label">Hardware ID</span>
                <div class="strip-value-row">
                    <code class="hardware-code text-slate-500 font-mono text-xs" [title]="camera.deviceHardwareID">{{ camera.deviceHardwareID }}</code>
                    <button type="button" class="copy-btn text-slate-400 hover:bg-slate-100" (click)="copy(camera.deviceHardwareID, 'Hardware ID')"
                            matTooltip="Copy Hardware ID">
                        <mat-icon class="!text-[14px]">content_copy</mat-icon>
                    </button>
                </div>
            </div>
            }
        </div>

        <!-- Technical Parameters Grid -->
        <div class="specs-grid">
            @if (camera.algorithmVersion) {
            <div class="spec-tile">
                <span class="spec-label">Algorithm Version</span>
                <span class="spec-value">{{ camera.algorithmVersion }}</span>
            </div>
            }

            @if (camera.systemVersion) {
            <div class="spec-tile">
                <span class="spec-label">System Version</span>
                <span class="spec-value">{{ camera.systemVersion }}</span>
            </div>
            }

            @if (camera.config?.confidenceReference) {
            <div class="spec-tile">
                <span class="spec-label">Confidence Scale</span>
                <span class="spec-value">{{ ConfidenceReferenceShowValue(camera.config?.confidenceReference!) }}</span>
            </div>
            }

            @if (cameraConfig?.timeZoneId) {
            <div class="spec-tile">
                <span class="spec-label">Timezone</span>
                <span class="spec-value">{{ cameraConfig?.timeZoneId }}</span>
            </div>
            }

            @if (camera.buildDate) {
            <div class="spec-tile">
                <span class="spec-label">Build Date</span>
                <span class="spec-value">{{ camera.buildDate | date:'mediumDate' }}</span>
            </div>
            }

            @if (lastHeartbeatAt) {
            <div class="spec-tile">
                <span class="spec-label">Last Seen Heartbeat</span>
                <span class="spec-value font-mono text-xs">{{ lastHeartbeatAt | date:'dd.MM.yyyy HH:mm:ss' }}</span>
            </div>
            }
        </div>

        <!-- Extra Camera Info (if present) -->
        @if (camera.cameraInfo) {
        <div class="info-callout">
            <mat-icon class="info-icon">info</mat-icon>
            <span class="info-text">{{ camera.cameraInfo }}</span>
        </div>
        }
    </mat-card-content>

    <!-- Card Action Buttons -->
    <div class="actions-wrapper">
        <div class="actions-left">
            <!-- Connect Button -->
            <button mat-flat-button color="primary" class="modern-action-btn primary-action"
                    (click)="onConnect()" [disabled]="isConnecting || isProcessing">
                <mat-icon [class.spinning]="isConnecting">{{ isConnecting ? 'sync' : 'link' }}</mat-icon>
                <span>{{ isConnecting ? 'Connecting…' : 'Connect' }}</span>
            </button>

            <!-- Start LPR Button -->
            <button mat-flat-button color="accent" class="modern-action-btn accent-action"
                    (click)="onStartLPRListener()" [disabled]="isStartingLPR || isProcessing">
                <mat-icon [class.spinning]="isStartingLPR">{{ isStartingLPR ? 'sync' : 'play_arrow' }}</mat-icon>
                <span>{{ isStartingLPR ? 'Starting…' : 'Start LPR' }}</span>
            </button>

            <!-- Detections Button -->
            <button mat-stroked-button color="primary" class="modern-action-btn"
                    (click)="onViewDetections()">
                <mat-icon>list</mat-icon>
                <span>Detections</span>
            </button>

            <!-- Events Button -->
            <button mat-stroked-button color="primary" class="modern-action-btn"
                    (click)="onViewEvents()">
                <mat-icon>event_note</mat-icon>
                <span>Events</span>
            </button>
        </div>

        <div class="actions-right">
            <!-- Edit Button -->
            <button mat-stroked-button color="primary" class="modern-action-btn"
                    (click)="onEdit()" [disabled]="isProcessing">
                <mat-icon>edit</mat-icon>
                <span>Edit</span>
            </button>

            <!-- Unassign Button -->
            <button mat-stroked-button color="warn" class="modern-action-btn warn-action"
                    (click)="onRemove()" [disabled]="isProcessing || isConnecting || isStartingLPR">
                <mat-icon>link_off</mat-icon>
                <span>Unassign</span>
            </button>
        </div>
    </div>
</mat-card>